// Sugalarm — mobile background catalog (cycle 171, step 1).
//
// The single source of truth for the 18 built-in MOBILE backgrounds: id,
// display name, group, and render kind. Consumed by the mobile-only
// MobileBackground layer (renders by `kind`) and the Settings → Appearance
// picker (groups + preview chips). All COLOR for each preset lives in
// `theme/tokens.css` under `[data-bg="<id>"]` / `.dark[data-bg="<id>"]`
// blocks (ported verbatim from the approved mockups); this module carries
// no hex. `aurora` is the default (the cycle-164 look) and renders the
// existing aurora layer — it has no `[data-bg]` block (no-op fallthrough).

import type { MobileBackground } from "./types";

// Cycle 5 adds "image" — a preset rendered as a hosted WebP photo (its
// light/dark/thumb srcs come from the theme-packs API, not tokens.css).
export type BgKind = "glow" | "mesh" | "gradient" | "pattern" | "scene" | "image";
export type BgFamily = "Calm" | "Scenic" | "Adventure";

export interface BgPreset {
  id: MobileBackground;
  name: string;
  family: BgFamily;
  kind: BgKind;
  /** Cycle 174: the pack this preset belongs to. "core" is free (the 18
   *  cycle-171 built-ins); premium packs (e.g. "legends") gate selection
   *  behind an entitlement. */
  pack: string;
}

export const BG_PRESETS: BgPreset[] = [
  // ── Calm (free · pack "core") ──
  { id: "aurora", name: "Aurora", family: "Calm", kind: "glow", pack: "core" },
  { id: "soft-mesh", name: "Soft Mesh", family: "Calm", kind: "mesh", pack: "core" },
  { id: "dawn-wash", name: "Dawn Wash", family: "Calm", kind: "gradient", pack: "core" },
  { id: "contour", name: "Contour", family: "Calm", kind: "pattern", pack: "core" },
  { id: "dot-grid", name: "Dot Grid", family: "Calm", kind: "pattern", pack: "core" },
  { id: "single-orb", name: "Single Orb", family: "Calm", kind: "glow", pack: "core" },
  { id: "aurora-cool", name: "Aurora · Cool", family: "Calm", kind: "glow", pack: "core" },
  { id: "sea-glass", name: "Sea Glass", family: "Calm", kind: "gradient", pack: "core" },
  { id: "morning-fog", name: "Morning Fog", family: "Calm", kind: "gradient", pack: "core" },
  { id: "warm-linen", name: "Warm Linen", family: "Calm", kind: "gradient", pack: "core" },
  { id: "lavender-mist", name: "Lavender Mist", family: "Calm", kind: "gradient", pack: "core" },
  { id: "sage-calm", name: "Sage Calm", family: "Calm", kind: "gradient", pack: "core" },
  // ── Scenic (free · pack "core") ──
  { id: "golden-plateau", name: "Golden Plateau", family: "Scenic", kind: "scene", pack: "core" },
  { id: "dawn-mist", name: "Dawn Mist", family: "Scenic", kind: "scene", pack: "core" },
  { id: "verdant-calm", name: "Verdant Calm", family: "Scenic", kind: "scene", pack: "core" },
  // ── Adventure (free · pack "core") ──
  { id: "heros-horizon", name: "Hero's Horizon", family: "Adventure", kind: "scene", pack: "core" },
  { id: "sacred-glade", name: "Sacred Glade", family: "Adventure", kind: "scene", pack: "core" },
  { id: "twilight-keep", name: "Twilight Keep", family: "Adventure", kind: "scene", pack: "core" },
  // ── Premium (pack "legends") — cycle 174 ──
  { id: "celestial", name: "Celestial", family: "Scenic", kind: "glow", pack: "legends" },
  // ── Premium image pack "relics" — cycle 5 ──
  // kind "image": rendered from a hosted WebP (srcs via the theme-packs
  // API); the picker swatch shows the thumb. family is cosmetic for premium
  // packs (the picker groups them by pack, not family).
  { id: "relic-sword", name: "Sword", family: "Adventure", kind: "image", pack: "relics" },
  { id: "relic-shield", name: "Shield", family: "Adventure", kind: "image", pack: "relics" },
  { id: "relic-amulet", name: "Amulet", family: "Adventure", kind: "image", pack: "relics" },
];

export const BG_BY_ID: Record<string, BgPreset> = Object.fromEntries(
  BG_PRESETS.map((p) => [p.id, p]),
);

export const BG_FAMILIES: BgFamily[] = ["Calm", "Scenic", "Adventure"];

/** Scene presets get the shared ridgeline + a legibility scrim. */
export function isScene(id: MobileBackground): boolean {
  return BG_BY_ID[id]?.kind === "scene";
}

/** Cycle 5: image presets render a hosted WebP layer (with an aurora
 *  fallback) instead of a tokenized gradient/scene. */
export function isImage(id: MobileBackground): boolean {
  return BG_BY_ID[id]?.kind === "image";
}

/** Cycle 174: the pack a preset belongs to ("core" for free / unknown). */
export function presetPack(id: MobileBackground): string {
  return BG_BY_ID[id]?.pack ?? "core";
}

/** Cycle 174: the preset to actually RENDER given the user's owned packs.
 *  A free ("core") preset always renders; a premium preset renders only
 *  if its pack is owned, otherwise it falls back to "aurora" (so a revoked
 *  entitlement never breaks the app — the stored selection is unchanged
 *  and re-renders the moment the pack is re-owned). */
export function effectiveBg(
  selected: MobileBackground,
  ownedPackIds: ReadonlySet<string>,
  // Cycle 189: dynamic preset→pack resolver (from the loaded catalog) so a
  // runtime-minted image preset's owning pack is honored. Falls back to the
  // static code mapping when the preset isn't in a loaded pack.
  resolvePack?: (id: string) => string | undefined,
  // Cycle 192: once the catalog has loaded, a selection that is no longer
  // OFFERED — a Core-hidden built-in, or a deleted upload — also falls back
  // to aurora (the stored value is left intact and re-renders if it's
  // offered again). Gated on `loaded` so a cold mount (catalog not yet
  // fetched) never flashes aurora for a still-valid selection.
  opts?: { loaded?: boolean; offered?: ReadonlySet<string> },
): MobileBackground {
  const pack = resolvePack?.(selected) ?? presetPack(selected);
  if (!(pack === "core" || ownedPackIds.has(pack))) return "aurora";
  if (
    selected !== "aurora" &&
    opts?.loaded &&
    opts.offered &&
    !opts.offered.has(selected)
  ) {
    return "aurora";
  }
  return selected;
}
